<template>
  <BModal
    :id="modalId"
    :model-value="modelValue"
    :size="bootstrapSize"
    :no-close-on-backdrop="!isBackdropClosable"
    :dialog-class="[
      `ui-modal ui-modal--${size} ui-modal--${tone}`,
      { 'ui-modal--fullscreen-mobile': fullscreenMobile },
    ]"
    content-class="ui-modal__content"
    header-class="ui-modal__header-wrap"
    :body-class="['ui-modal__body', { 'ui-modal__body--soft': bodyTone === 'soft' }]"
    footer-class="ui-modal__footer"
    :no-footer="!$slots.footer"
    @update:model-value="onUpdateModelValue"
    @show="$emit('show')"
    @shown="onShown"
    @hide="$emit('hide')"
    @hidden="$emit('hidden')"
  >
    <template #header>
      <div class="ui-modal__header" :class="{ 'ui-modal__header--flow': hasHeaderFlow() }">
        <span v-if="icon && hasHeaderFlow()" class="ui-modal__icon">
          <component :is="icon" :size="26" :stroke-width="2" aria-hidden="true" />
        </span>

        <div class="ui-modal__titlebar">
          <span v-if="icon && !hasHeaderFlow()" class="ui-modal__icon">
            <component :is="icon" :size="26" :stroke-width="2" aria-hidden="true" />
          </span>

          <h2 :id="`${modalId}-label`" class="ui-modal__title">{{ title }}</h2>

          <slot name="title-addon" />
        </div>

        <p v-if="hasSubtitle()" :id="`${modalId}-subtitle`" class="ui-modal__subtitle">
          <slot name="subtitle">{{ subtitle }}</slot>
        </p>

        <div v-if="$slots['header-actions']" class="ui-modal__actions">
          <slot name="header-actions" />
        </div>
      </div>

      <button
        v-if="!hideClose"
        type="button"
        class="ui-modal__close"
        :aria-label="closeLabel ?? $t('Close')"
        @click="onUpdateModelValue(false)"
      >
        <X :size="22" :stroke-width="2.2" aria-hidden="true" />
      </button>
    </template>

    <slot />

    <template v-if="$slots.footer" #footer>
      <slot name="footer" />
    </template>
  </BModal>
</template>

<script setup lang="ts">
import { computed, nextTick, useId, useSlots } from "vue";
import type { Component } from "vue";
import { BModal } from "bootstrap-vue-next";
import { X } from "lucide-vue-next";

const props = withDefaults(
  defineProps<{
    modelValue: boolean;
    title: string;
    subtitle?: string;
    size?: "s" | "m" | "l" | "xl";
    tone?: "info" | "danger" | "success";
    icon?: Component;
    bodyTone?: "default" | "soft";
    closeOnOverlay?: boolean;
    initialFocus?: string;
    closeLabel?: string;
    fullscreenMobile?: boolean;
    hideClose?: boolean;
  }>(),
  {
    subtitle: undefined,
    size: "m",
    tone: "info",
    icon: undefined,
    bodyTone: "default",
    closeOnOverlay: undefined,
    initialFocus: undefined,
    closeLabel: undefined,
    fullscreenMobile: false,
    hideClose: false,
  },
);

const emit = defineEmits<{
  "update:modelValue": [value: boolean];
  show: [];
  shown: [];
  hide: [];
  hidden: [];
}>();

const slots = useSlots();

const modalId = `ui-modal-${useId()}`;

const hasSubtitle = (): boolean => Boolean(props.subtitle) || Boolean(slots.subtitle);

const hasHeaderFlow = (): boolean => hasSubtitle() && props.icon !== undefined;

const bootstrapSize = computed(() => (props.size === "s" ? "sm" : "md"));

const isBackdropClosable = computed(() => props.closeOnOverlay ?? props.tone !== "danger");

const onUpdateModelValue = (value: boolean) => {
  emit("update:modelValue", value);
};

const onShown = async () => {
  emit("shown");

  if (!props.initialFocus) {
    return;
  }

  await nextTick();

  document.getElementById(modalId)?.querySelector<HTMLElement>(props.initialFocus)?.focus();
};
</script>
